import { Link } from "react-router-dom";
import { ArrowUpRight, Users, Dumbbell, Clock, HeartHandshake, ClipboardCheck } from "lucide-react";

const INCLUDES = [
  {
    icon: Users,
    title: "Two coached sessions each week",
    body: "Small groups. Friendly coaches. Sensible progressions. You'll be told what to do, taught how to do it, and cheered on while you do it.",
  },
  {
    icon: Dumbbell,
    title: "Great classes",
    body: "Unlimited access to our classes — Ascent, Zones, Blast and Kombat. Something for the days you want to sweat, and the days you don't.",
  },
  {
    icon: ClipboardCheck,
    title: "A clear training plan",
    body: "You'll always know what you're doing this week and why. No random workouts, no guesswork. Just steady progress.",
  },
  {
    icon: HeartHandshake,
    title: "Support and accountability",
    body: "A monthly one-to-one with your coach, and coaches who actually notice when you miss a session. This is what keeps most people going.",
  },
  {
    icon: Clock,
    title: "Full gym access",
    body: "Come in and train on your own whenever you like during opening hours. Weekdays, weekends, before work, after work — it's your gym.",
  },
  {
    icon: Users,
    title: "A friendly 40+ community",
    body: "Real people, real conversations, and a proper community you'll enjoy being part of. Cake at Christmas is possible.",
  },
];

const WHO_ITS_FOR = [
  "Are 40+ and want to feel fitter and stronger.",
  "Want to enjoy training — not dread it.",
  "Would like coaching without the intimidation.",
  "Miss having a community around your fitness.",
  "Want a plan to follow, not another random workout.",
  "Are ready to actually turn up twice a week.",
];

const PILLARS = [
  "Strength & Performance",
  "Mobility & Movement",
  "Recovery & Longevity",
  "Nutrition & Lifestyle",
  "Mindset & Accountability",
  "Community & Support",
];

export default function KoreCoachedContent() {
  return (
    <div data-testid="kore-coached-details">
      {/* Overview */}
      <section className="bg-white py-24">
        <div className="container-kore grid md:grid-cols-12 gap-14">
          <div className="md:col-span-4">
            <span className="eyebrow">The flagship</span>
            <h2 className="display-2 mt-4 text-kore-forest">More than access.</h2>
          </div>
          <div className="md:col-span-7 md:col-start-6 space-y-5 text-kore-ink/85 leading-relaxed">
            <p className="text-lg font-light text-kore-forest">
              Kore Coaching is Kore Wellbeing's flagship membership, designed for people who want more than just access to a gym.
            </p>
            <p>
              It combines expert coaching, structured training, accountability, and a welcoming community to help members build strength, improve their health, and stay consistent.
            </p>
            <p>
              Rather than being left to figure things out alone, members are supported every step of the way by experienced coaches who provide guidance, motivation, and accountability.
            </p>
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="py-24">
        <div className="container-kore grid md:grid-cols-12 gap-14">
          <div className="md:col-span-4">
            <span className="eyebrow">Who it's for</span>
            <h2 className="display-2 mt-4 text-kore-forest">Adults who want<br/>to do this properly.</h2>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <ul className="space-y-4">
              {WHO_ITS_FOR.map((w, i) => (
                <li key={w} className="flex items-start gap-4 border-b border-kore-gold/20 pb-4">
                  <span className="text-kore-gold text-xs font-medium mt-1.5">0{i + 1}</span>
                  <span className="text-kore-ink/85">{w}</span>
                </li>
              ))}
            </ul>
            <p className="mt-8 text-kore-ink/70 text-sm">Whether you're completely new to training or have years of experience, Kore Coaching provides the structure and support to help you progress.</p>
          </div>
        </div>
      </section>

      {/* What's included — detailed */}
      <section className="bg-white py-24">
        <div className="container-kore">
          <div className="max-w-2xl mb-14">
            <span className="eyebrow">Included every month</span>
            <h2 className="display-2 mt-4 text-kore-forest">Everything you need,<br/>nothing you don't.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-x-10 gap-y-12">
            {INCLUDES.map((it) => {
              const Icon = it.icon;
              return (
                <div key={it.title} className="flex gap-5">
                  <div className="shrink-0 w-11 h-11 rounded-full border border-kore-gold/40 flex items-center justify-center text-kore-forest">
                    <Icon size={18} />
                  </div>
                  <div>
                    <h3 className="font-display text-2xl text-kore-forest leading-tight">{it.title}</h3>
                    <p className="mt-2 text-kore-ink/75 leading-relaxed text-sm">{it.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* The Kore Difference */}
      <section className="bg-kore-forest text-kore-warm py-24">
        <div className="container-kore grid md:grid-cols-12 gap-14">
          <div className="md:col-span-5">
            <span className="eyebrow text-kore-light">The Kore Difference</span>
            <h2 className="display-2 mt-4">Consistency<br/>over extremes.</h2>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <p className="text-lg text-kore-warm/85 font-light leading-relaxed">
              At Kore, we believe long-term health isn't built through extremes. It's built through consistency, expert coaching, and a community that genuinely wants to see you succeed.
            </p>
            <p className="mt-4 text-kore-warm/75 leading-relaxed">Everything we do is centred around six key pillars:</p>
            <ul className="mt-8 space-y-3">
              {PILLARS.map((p, i) => (
                <li key={p} className="flex items-center gap-5 border-b border-kore-light/20 py-3">
                  <span className="text-kore-light text-xs">0{i + 1}</span>
                  <span className="font-display text-xl">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Member Experience */}
      <section className="py-24">
        <div className="container-kore grid md:grid-cols-12 gap-14 items-start">
          <div className="md:col-span-5">
            <span className="eyebrow">Member experience</span>
            <h2 className="display-2 mt-4 text-kore-forest">Known by name.<br/>Coached by craft.</h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 text-kore-ink/85 leading-relaxed">
            <p>
              Kore is a friendly, supportive environment where members are known by name. Whether you're walking through the doors for the first time or have been training for years, you'll find expert coaching, encouragement, and a community that helps you become stronger, healthier, and more confident.
            </p>
          </div>
        </div>
      </section>

      {/* Summary CTA */}
      <section className="bg-kore-light/30 py-24">
        <div className="container-kore max-w-3xl text-center">
          <span className="eyebrow">In summary</span>
          <h2 className="display-2 mt-4 text-kore-forest">One complete membership.</h2>
          <p className="mt-6 text-kore-ink/80 leading-relaxed">
            Kore Coaching combines small group coaching, unlimited classes, open gym access, lifestyle support, monthly check-ins, and accountability into one complete membership. Designed to give you the structure, support, and community you need to create lasting results — and enjoy the process along the way.
          </p>
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <Link to="/book" className="btn-primary">View the timetable <ArrowUpRight size={14} /></Link>
            <Link to="/contact" className="btn-ghost">Book a consultation</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
